import { z } from 'zod';
export const SERVICE_UNITS = {
    site_generation: {
        singular: 'site generation',
        plural: 'site generations',
        /** Names the service on the payment itself, so it must describe what was sold. */
        service: 'Website generation service',
    },
    video_background: {
        singular: 'video background',
        plural: 'video backgrounds',
        service: 'Video background service',
    },
};
export const SERVICE_PACKS = [
    /**
     * Video backgrounds, fulfilled by hand for now.
     *
     * The unit is one 60-second video, so a pack is a count of videos like every
     * other pack is a count of sites. That is what makes two sizes possible at
     * all, and what will let an automated pipeline draw one video at a time from
     * the same entitlement later without changing anything a customer bought.
     *
     * $188.67 a video at three, $137.60 at five, $123.90 at ten — a 34% volume
     * discount across the range, steeper than the site ladder because the second
     * and third video for one customer cost far less to make than the first.
     */
    {
        sku: 'pack_video_3',
        unit: 'video_background',
        label: '3 videos',
        generations: 3,
        priceMinor: 56_600,
        currency: 'USD',
        validityMonths: 12,
    },
    {
        sku: 'pack_video_5',
        unit: 'video_background',
        label: '5 videos',
        generations: 5,
        priceMinor: 68_800,
        currency: 'USD',
        validityMonths: 12,
    },
    {
        sku: 'pack_video_10',
        unit: 'video_background',
        label: '10 videos',
        generations: 10,
        priceMinor: 123_900,
        currency: 'USD',
        validityMonths: 12,
    },
    {
        sku: 'pack_starter',
        label: 'Starter',
        generations: 10,
        priceMinor: 2900,
        currency: 'USD',
        validityMonths: 12,
    },
    {
        sku: 'pack_standard',
        label: 'Standard',
        generations: 30,
        priceMinor: 6900,
        currency: 'USD',
        validityMonths: 12,
        popular: true,
    },
    {
        sku: 'pack_pro',
        label: 'Pro',
        generations: 100,
        priceMinor: 19900,
        currency: 'USD',
        validityMonths: 12,
    },
    /**
     * The quantity here is a judgement, not an instruction: the price was given,
     * the count was not. 250 continues the curve — $2.90, $2.30, $1.99, $1.60 —
     * so the biggest pack is the one that actually rewards volume. Change the
     * number here and the whole product follows; nothing else hardcodes it.
     */
    {
        sku: 'pack_agency',
        label: 'Agency',
        generations: 250,
        priceMinor: 36600,
        currency: 'USD',
        validityMonths: 12,
    },
    /**
     * Same judgement as Agency above, one rung further: the price was given, the
     * count chosen to keep the ladder going — $2.90, $2.30, $1.99, $1.60, $1.33.
     * A top pack that did not beat the one below it per site would be a tier
     * nobody has a reason to pick.
     */
    {
        sku: 'pack_enterprise',
        label: 'Enterprise',
        generations: 600,
        priceMinor: 90000,
        currency: 'USD',
        validityMonths: 12,
    },
];
/**
 * The former welcome grant. **No longer issued.**
 *
 * Kept because accounts created while it existed still hold an entitlement with
 * this SKU, and the history table tells a free grant apart from a purchase by
 * exactly that. Deleting it would relabel those rows as payments the customer
 * never made — the bug this constant now exists to prevent.
 *
 * Never in `SERVICE_PACKS`, so it has never been purchasable.
 */
export const SIGNUP_GRANT = {
    sku: 'grant_signup',
    label: 'Welcome',
    generations: 1,
    priceMinor: 0,
    currency: 'USD',
    validityMonths: 1,
};
/**
 * A grant issued by support, sized per decision rather than from a price list.
 *
 * Its own SKU so the history table can label it honestly: "Manual adjustment",
 * never "Service pack" — a purchase the customer did not make must not appear
 * in their history, or in the ledger an underwriter reads. Expiry is short for
 * the same reason the signup grant's was: a free grant that never expires is
 * the same deposit-shaped thing as a paid one.
 */
export const ADMIN_GRANT_SKU = 'grant_admin';
export const adminGrant = (generations) => ({
    sku: ADMIN_GRANT_SKU,
    label: 'Granted by support',
    generations,
    priceMinor: 0,
    currency: 'USD',
    validityMonths: 3,
});
/** The unit a pack sells, with the historical default made explicit. */
export const unitOf = (pack) => pack.unit ?? 'site_generation';
/** Only the packs denominated in a given unit — what a storefront section is. */
export const packsForUnit = (unit) => SERVICE_PACKS.filter((pack) => unitOf(pack) === unit);
export const findServicePack = (sku) => sku === SIGNUP_GRANT.sku ? SIGNUP_GRANT : SERVICE_PACKS.find((pack) => pack.sku === sku);
export const servicePackSkuSchema = z
    .string()
    .refine((sku) => SERVICE_PACKS.some((pack) => pack.sku === sku), 'unknown service pack');
/**
 * Edits included with each site generation.
 *
 * A generation buys the site plus three revisions of it. The fourth edit draws
 * another generation from the pack.
 *
 * Derived rather than stored: a project's allowance is
 * `generations_consumed × 3`, measured against the edits already delivered for
 * that project. Nothing mutable has to be kept in step, and the entitlement
 * ledger stays append-only — which is the property that makes it worth showing
 * an underwriter.
 */
export const EDITS_INCLUDED_PER_GENERATION = 3;
/** Units drawn for each kind of work, before the edit allowance is applied. */
export const GENERATION_COST = {
    create: 1,
    edit: 1,
    repair: 0,
};
/** Wall-clock validity, from the moment a pack is purchased. */
export const expiryFor = (pack, purchasedAt) => {
    const expires = new Date(purchasedAt);
    expires.setMonth(expires.getMonth() + pack.validityMonths);
    return expires;
};
// ── Customer-facing vocabulary ───────────────────────────────────────────────
/**
 * Words that must never reach a customer-facing surface.
 *
 * Reviewers screenshot the pricing page; that screenshot is the artifact that
 * decides whether this reads as a service pack or a wallet. `vocabulary.spec.ts`
 * fails the build if any of these appear in the SPA, the landing page or the
 * checkout descriptors, which makes the rule enforceable rather than aspirational.
 */
export const FORBIDDEN_CUSTOMER_TERMS = [
    'credit',
    'credits',
    'balance',
    'top up',
    'top-up',
    'topup',
    'wallet',
    'refill',
    'recharge',
    '充值',
    '余额',
];
/**
 * Quantity phrasings that read as a balance, whatever unit they use.
 *
 * "build" is deliberately not a forbidden word — the verb is ordinary English
 * and banning it flags "what people build with it" and `pnpm build`. A rule that
 * cries wolf gets switched off, and then it protects nothing. What matters is
 * the *quantity* form, which is what makes a number look like an account.
 */
export const FORBIDDEN_QUANTITY_PATTERNS = [
    /\bbuilds?\s+(remaining|left|available)\b/i,
    /\b\d+\s+builds?\b/i,
    /\bremaining\s+builds?\b/i,
    /[¥$€£]\s?\d+(\.\d+)?\s+(remaining|left)\b/i,
];
/** How the same ideas are said instead. */
export const GENERATION_VOCABULARY = {
    unit: 'site generation',
    unitPlural: 'site generations',
    remaining: 'generations remaining',
    purchase: 'Buy a pack',
    /** Chinese storefront wording — 网站生成服务, never 充值 or 余额. */
    storefrontZh: '网站生成服务',
};
/**
 * "1 site generation", "3 site generations".
 *
 * Every customer-facing count goes through this. The quantities are config —
 * a pack size, a grant, the cost of an edit — so hardcoding either the number
 * or its plural form produces copy that is wrong the first time somebody
 * changes a constant, and wrong in the one place a customer is deciding
 * whether to pay us.
 */
export const generationsLabel = (count) => unitLabel(count, 'site_generation');
/** "1 video background", "3 site generations" — the count and its own noun. */
export const unitLabel = (count, unit) => {
    const words = SERVICE_UNITS[unit];
    return `${count} ${count === 1 ? words.singular : words.plural}`;
};
/**
 * What the customer sees on the payment, and on the card statement line behind
 * it — so it has to describe the thing they actually bought.
 *
 * This used to say "Website generation service — N site generations" for every
 * pack, including the video ones: a $590 video purchase was recorded in Stripe
 * as "3 site generations". Wrong to the buyer, and wrong to a payment reviewer
 * comparing the description against what was delivered.
 */
export const orderDescription = (pack) => {
    const unit = unitOf(pack);
    return `${SERVICE_UNITS[unit].service} — ${unitLabel(pack.generations, unit)}`;
};
/**
 * The terms shown under the line item at checkout.
 *
 * Derived for the same reason the title is: the previous wording said "A pack
 * of site generations ... expires 12 months from purchase" for every pack,
 * which was two claims that could be wrong at once — the wrong noun for a
 * video pack, and a validity period typed as prose next to a `validityMonths`
 * field nobody was reading.
 *
 * The non-transferable / no-cash-value sentence stays fixed on purpose: it is
 * the stored-value carve-out, and it applies to every pack identically.
 */
export const orderTerms = (pack) => {
    const unit = unitOf(pack);
    const delivery = unit === 'video_background' ? 'produced to your brief' : 'delivered on demand';
    return (`A pack of ${SERVICE_UNITS[unit].plural}, ${delivery}. Non-transferable, ` +
        `expires ${pack.validityMonths} months from purchase, no cash value.`);
};
/**
 * Statement descriptor. Deliberately "SVC", never "CREDITS".
 *
 * Capped at 22 characters because that is the card-network limit, and truncation
 * is decided here rather than by whichever processor happens to be in front.
 */
export const statementDescriptor = (brand = 'GTEXAI') => `${brand} WEBSITE SVC`.slice(0, 22);
//# sourceMappingURL=service-packs.js.map